import { h } from './ui';

export interface ComboItem {
  id: string;
  value: string; // Text, der nach der Auswahl im Eingabefeld steht
  label: string; // Text in der Liste
  hint?: string;
}

export interface Combobox {
  el: HTMLElement;
  input: HTMLInputElement;
  getText(): string;
  getSelected(): ComboItem | null;
}

/** Combobox mit Freitext-Eingabe; die Reihenfolge der Einträge bleibt erhalten. */
export function createCombobox(
  items: ComboItem[],
  opts: { value?: string; placeholder?: string } = {},
): Combobox {
  const input = h('input', {
    type: 'text',
    class: 'combo-input',
    autocomplete: 'off',
    role: 'combobox',
    'aria-autocomplete': 'list',
    placeholder: opts.placeholder ?? '',
    value: opts.value ?? '',
  });
  const list = h('ul', { class: 'combo-list', role: 'listbox' });
  list.hidden = true;

  let shown: ComboItem[] = [];
  let active = -1;

  const close = (): void => {
    list.hidden = true;
    active = -1;
  };

  const choose = (it: ComboItem): void => {
    input.value = it.value;
    close();
    input.dispatchEvent(new Event('change', { bubbles: true }));
  };

  const open = (filter: boolean): void => {
    const q = filter ? input.value.trim().toLowerCase() : '';
    shown = items.filter((i) => !q || i.label.toLowerCase().includes(q));
    list.replaceChildren(
      ...shown.map((it) =>
        h(
          'li',
          {
            role: 'option',
            onmousedown: (e: Event) => {
              e.preventDefault();
              choose(it);
            },
          },
          h('span', { class: 'combo-name' }, it.label),
          it.hint ? h('span', { class: 'combo-hint' }, it.hint) : null,
        ),
      ),
    );
    active = -1;
    list.hidden = shown.length === 0;
  };

  const highlight = (): void => {
    Array.from(list.children).forEach((li, i) => li.classList.toggle('active', i === active));
    (list.children[active] as HTMLElement | undefined)?.scrollIntoView({ block: 'nearest' });
  };

  const toggle = h(
    'button',
    {
      type: 'button',
      class: 'combo-toggle',
      tabindex: '-1',
      'aria-label': 'Auswahlliste öffnen',
      onmousedown: (e: Event) => e.preventDefault(),
      onclick: () => {
        if (list.hidden) {
          input.focus();
          open(false);
        } else {
          close();
        }
      },
    },
    '▾',
  );

  input.addEventListener('input', () => open(true));
  input.addEventListener('click', () => {
    if (list.hidden) open(false);
  });
  input.addEventListener('blur', close);
  input.addEventListener('keydown', (e: KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (list.hidden) open(false);
      else {
        active = Math.min(active + 1, shown.length - 1);
        highlight();
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      active = Math.max(active - 1, 0);
      highlight();
    } else if (e.key === 'Enter' && !list.hidden && active >= 0) {
      e.preventDefault();
      choose(shown[active]);
    } else if (e.key === 'Escape' && !list.hidden) {
      e.preventDefault();
      e.stopPropagation();
      close();
    }
  });

  return {
    el: h('div', { class: 'combo' }, input, toggle, list),
    input,
    getText: () => input.value.trim(),
    getSelected: () => {
      const t = input.value.trim().toLowerCase();
      return items.find((i) => i.value.toLowerCase() === t) ?? null;
    },
  };
}
